import { formatDate } from '../utils/dateConverter';
import React, { useCallback, useEffect, useState } from 'react';
import api from '../services/api';
import { extractApiError } from '../utils/apiError';
import '../styles/AcquisitionDetail.css';
import { useToast, useConfirm } from '../context/UIContext';
import {
    FUNNEL_STAGES,
    LOST_STAGE,
    dayDiff,
    daysInStage,
    dealTypeLabel,
    dealValue,
    followUpState,
    formatEur,
    isOpen,
    leadSourceLabel,
    lineTotal,
    nextStage,
    positionsTotal,
    probabilityOf,
    stageClass,
    stageIndex,
    stageLabel,
    weightedValue,
} from '../utils/salesPipeline';

const ACTIVITY_ICONS = {
    note:    '📝',
    call:    '📞',
    email:   '✉️',
    meeting: '🤝',
};

const PRIORITY_CLASSES = { High: 'prio-high', Medium: 'prio-medium', Low: 'prio-low' };

/** Local ISO date (yyyy-mm-dd) `days` from today — no timezone drift. */
const isoInDays = (days) => {
    const d = new Date();
    d.setDate(d.getDate() + days);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const FOLLOW_UP_TEXT = {
    overdue: (days) => `überfällig seit ${Math.abs(days)} Tag${Math.abs(days) === 1 ? '' : 'en'}`,
    today: () => 'heute fällig',
    upcoming: (days) => `in ${days} Tag${days === 1 ? '' : 'en'}`,
};

export default function AcquisitionDetail({ acquisition, onClose, onEdit, onDelete, onFileChange, onPatch }) {
    const toast = useToast();
    const confirm = useConfirm();
    const [activities, setActivities] = useState([]);
    const [activityType, setActivityType] = useState('call');
    const [activityContent, setActivityContent] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [fileError, setFileError] = useState('');
    const [lostReason, setLostReason] = useState('');
    const [askLostReason, setAskLostReason] = useState(false);

    const loadActivities = useCallback(() => {
        if (!acquisition) return;
        api.get(`/ProjectAcquisition/${acquisition.id}/activities`)
            .then((res) => setActivities(res.data))
            .catch(() => {});
    }, [acquisition]);

    useEffect(() => {
        loadActivities();
    }, [loadActivities]);

    useEffect(() => {
        setAskLostReason(false);
        setLostReason(acquisition?.lostReason || '');
    }, [acquisition]);

    const patch = (payload) => {
        if (!onPatch) return Promise.resolve();
        return onPatch(acquisition.id, payload)
            .catch(err => toast(extractApiError(err, 'Änderung konnte nicht gespeichert werden.')));
    };

    const handleStageClick = (stageKey) => {
        if (stageKey === acquisition.status) return;
        if (stageKey === LOST_STAGE) { setAskLostReason(true); return; }
        patch({ status: stageKey });
    };

    const handleMarkLost = () => {
        if (!onPatch) return;
        onPatch(acquisition.id, { status: LOST_STAGE, lost_reason: lostReason.trim() || null })
            .then(() => setAskLostReason(false))
            .catch(err => toast(extractApiError(err, 'Änderung konnte nicht gespeichert werden.')));
    };

    const handleAddActivity = () => {
        if (!activityContent.trim()) return;
        setSubmitting(true);
        api.post(`/ProjectAcquisition/${acquisition.id}/activities`, {
            type: activityType,
            content: activityContent.trim(),
        })
            .then(() => {
                setActivityContent('');
                loadActivities();
                // A logged call/mail/meeting bumps the contact counter server-side —
                // refresh so the Kaltakquise block shows the new count.
                onFileChange?.();
            })
            .catch(err => toast(extractApiError(err, 'Aktivität konnte nicht gespeichert werden.')))
            .finally(() => setSubmitting(false));
    };

    const handleDelete = async () => {
        if (!await confirm(`"${acquisition.projectTitle}" wirklich löschen?`, { confirmLabel: 'Löschen', danger: true })) return;
        onDelete(acquisition.id);
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setFileError('');
        setUploading(true);
        const formData = new FormData();
        formData.append('file', file);
        try {
            await api.post(`/ProjectAcquisition/${acquisition.id}/offer-file`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            onFileChange?.();
        } catch (err) {
            const msg = err.response?.data?.detail || 'Upload fehlgeschlagen.';
            setFileError(typeof msg === 'string' ? msg : JSON.stringify(msg));
        } finally {
            setUploading(false);
            e.target.value = '';
        }
    };

    const handleFileDownload = async () => {
        try {
            const res = await api.get(`/ProjectAcquisition/${acquisition.id}/offer-file`);
            window.open(res.data.url, '_blank', 'noopener,noreferrer');
        } catch (err) {
            toast(extractApiError(err, 'Download-Link konnte nicht generiert werden.'));
        }
    };

    const handleFileDelete = async () => {
        if (!await confirm('Angebotsdatei wirklich entfernen?', { confirmLabel: 'Löschen', danger: true })) return;
        try {
            await api.delete(`/ProjectAcquisition/${acquisition.id}/offer-file`);
            onFileChange?.();
        } catch (err) {
            toast(extractApiError(err, 'Löschen fehlgeschlagen.'));
        }
    };

    if (!acquisition) return null;

    const fmt = (n) => (n != null ? formatEur(n) : '—');
    const fmtDate = (d) => (d ? formatDate(d) : '—');

    const items = acquisition.lineItems || [];
    const currentIdx = stageIndex(acquisition.status);
    const upcoming = nextStage(acquisition.status);
    const followUp = followUpState(acquisition);
    const followUpDays = acquisition.nextFollowUpDate ? dayDiff(acquisition.nextFollowUpDate) : null;
    const stageAge = daysInStage(acquisition);

    return (
        <div className="detail-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
            <div className="detail-drawer">
                <div className="detail-header">
                    <div className="detail-header-meta">
                        <span className={`detail-stage ${stageClass(acquisition.status)}`}>
                            {stageLabel(acquisition.status)}
                        </span>
                        <span className={`detail-prio ${PRIORITY_CLASSES[acquisition.priority] || 'prio-medium'}`}>
                            {acquisition.priority}
                        </span>
                        <span className={`detail-type type-${acquisition.dealType || 'project'}`}>
                            {dealTypeLabel(acquisition.dealType)}
                        </span>
                    </div>
                    <button className="detail-close" onClick={onClose} aria-label="Schließen">✕</button>
                </div>

                <div className="detail-body">
                    <h2 className="detail-title">{acquisition.projectTitle}</h2>
                    <div className="detail-company">{acquisition.companyName}</div>

                    <section className="detail-section detail-section--first">
                        <h4>Prozess</h4>
                        <div className="stepper">
                            {FUNNEL_STAGES.map((stage, idx) => {
                                const done = currentIdx >= 0 && idx <= currentIdx;
                                const isCurrent = stage.key === acquisition.status;
                                return (
                                    <button
                                        key={stage.key}
                                        type="button"
                                        className={`stepper-step${done ? ' is-done' : ''}${isCurrent ? ' is-current' : ''}`}
                                        onClick={() => handleStageClick(stage.key)}
                                        title={`${stage.hint} — klicken, um den Deal hierhin zu setzen`}
                                    >
                                        <span className="stepper-dot">{idx + 1}</span>
                                        <span className="stepper-label">{stage.label}</span>
                                    </button>
                                );
                            })}
                        </div>

                        <div className="stepper-actions">
                            {upcoming && isOpen(acquisition) && (
                                <button className="button" onClick={() => patch({ status: upcoming.key })}>
                                    Weiter zu „{upcoming.label}“
                                </button>
                            )}
                            {acquisition.status !== LOST_STAGE && (
                                <button className="button button-secondary" onClick={() => setAskLostReason(true)}>
                                    Als verloren markieren
                                </button>
                            )}
                            {stageAge !== null && (
                                <span className="detail-inline-meta">
                                    {stageAge} Tag{stageAge === 1 ? '' : 'e'} in dieser Phase
                                </span>
                            )}
                        </div>

                        {askLostReason && (
                            <div className="lost-form">
                                <label className="form-label">Verlustgrund</label>
                                <input
                                    type="text"
                                    className="input"
                                    placeholder="z.B. Preis, Timing, Wettbewerber …"
                                    value={lostReason}
                                    onChange={(e) => setLostReason(e.target.value)}
                                />
                                <div className="lost-form-actions">
                                    <button className="button-danger" onClick={handleMarkLost}>Verloren speichern</button>
                                    <button className="button button-secondary" onClick={() => setAskLostReason(false)}>
                                        Abbrechen
                                    </button>
                                </div>
                            </div>
                        )}
                    </section>

                    <div className="detail-metrics">
                        <div className="detail-metric">
                            <span className="detail-metric-label">
                                Dealwert{items.length ? ' (aus Positionen)' : ''}
                            </span>
                            <span className="detail-metric-value">{fmt(dealValue(acquisition))}</span>
                        </div>
                        <div className="detail-metric">
                            <span className="detail-metric-label">
                                Gewichtet ({probabilityOf(acquisition)} %)
                            </span>
                            <span className="detail-metric-value">{fmt(weightedValue(acquisition))}</span>
                        </div>
                        <div className="detail-metric">
                            <span className="detail-metric-label">Abschlussdatum</span>
                            <span className="detail-metric-value">{fmtDate(acquisition.estimatedCloseDate)}</span>
                        </div>
                        {acquisition.offerAmount != null && (
                            <div className="detail-metric">
                                <span className="detail-metric-label">Angebotsbetrag</span>
                                <span className="detail-metric-value">{fmt(acquisition.offerAmount)}</span>
                            </div>
                        )}
                        <div className="detail-metric">
                            <span className="detail-metric-label">Letztes Update</span>
                            <span className="detail-metric-value">{fmtDate(acquisition.updatedAt)}</span>
                        </div>
                    </div>

                    <section className="detail-section">
                        <h4>Akquise</h4>
                        <div className="detail-contact-grid">
                            <div className="detail-contact-item">
                                <span className="detail-label">Quelle</span>
                                <span>{leadSourceLabel(acquisition.leadSource)}</span>
                            </div>
                            <div className="detail-contact-item">
                                <span className="detail-label">Versuche</span>
                                <span>
                                    {acquisition.contactAttempts || 0} Kontaktversuch{(acquisition.contactAttempts || 0) === 1 ? '' : 'e'}
                                    {acquisition.lastContactDate && ` · letzter am ${fmtDate(acquisition.lastContactDate)}`}
                                </span>
                            </div>
                            <div className="detail-contact-item">
                                <span className="detail-label">Verantwortlich</span>
                                <span>{acquisition.ownerName || '—'}</span>
                            </div>
                        </div>

                        <div className={`followup-box is-${followUp}`}>
                            <div className="followup-text">
                                <span className="detail-label">Nächste Wiedervorlage</span>
                                <strong>
                                    {acquisition.nextFollowUpDate
                                        ? `${fmtDate(acquisition.nextFollowUpDate)} (${FOLLOW_UP_TEXT[followUp]?.(followUpDays) ?? ''})`
                                        : 'nicht gesetzt'}
                                </strong>
                            </div>
                            {isOpen(acquisition) && (
                                <div className="followup-actions">
                                    <button className="button button-secondary" onClick={() => patch({ next_follow_up_date: isoInDays(3) })}>
                                        +3 Tage
                                    </button>
                                    <button className="button button-secondary" onClick={() => patch({ next_follow_up_date: isoInDays(7) })}>
                                        +1 Woche
                                    </button>
                                    <button className="button button-secondary" onClick={() => patch({ next_follow_up_date: isoInDays(14) })}>
                                        +2 Wochen
                                    </button>
                                    {acquisition.nextFollowUpDate && (
                                        <button className="button button-secondary" onClick={() => patch({ next_follow_up_date: null })}>
                                            Entfernen
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    </section>

                    {items.length > 0 && (
                        <section className="detail-section">
                            <h4>Positionen</h4>
                            <div className="detail-pos-wrap">
                                <table className="detail-pos-table">
                                    <tbody>
                                        {items.map((item, idx) => (
                                            <tr key={idx}>
                                                <td>
                                                    <span className="detail-pos-desc">{item.description}</span>
                                                    {item.sub_description && (
                                                        <span className="detail-pos-sub">{item.sub_description}</span>
                                                    )}
                                                </td>
                                                <td className="detail-pos-qty">
                                                    {Number(item.quantity).toLocaleString('de-DE')} {item.unit}
                                                    {Number(item.discount_percent) > 0 && (
                                                        <span className="detail-pos-discount">−{item.discount_percent} %</span>
                                                    )}
                                                </td>
                                                <td className="detail-pos-total">
                                                    {formatEur(lineTotal(item), { decimals: 2 })}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr>
                                            <td colSpan={2}>Summe netto</td>
                                            <td className="detail-pos-total">
                                                {formatEur(positionsTotal(items), { decimals: 2 })}
                                            </td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </section>
                    )}

                    <section className="detail-section">
                        <h4>Ansprechpartner</h4>
                        <div className="detail-contact-grid">
                            <div className="detail-contact-item">
                                <span className="detail-label">Name</span>
                                <span>{acquisition.contactPerson || '—'}</span>
                            </div>
                            <div className="detail-contact-item">
                                <span className="detail-label">E-Mail</span>
                                {acquisition.contactEmail
                                    ? <a href={`mailto:${acquisition.contactEmail}`}>{acquisition.contactEmail}</a>
                                    : <span>—</span>}
                            </div>
                            <div className="detail-contact-item">
                                <span className="detail-label">Telefon</span>
                                {acquisition.contactPhone
                                    ? <a href={`tel:${acquisition.contactPhone}`}>{acquisition.contactPhone}</a>
                                    : <span>—</span>}
                            </div>
                        </div>
                    </section>

                    <section className="detail-section">
                        <h4>Details</h4>
                        <div className="detail-field">
                            <span className="detail-label">Beschreibung</span>
                            <p className="detail-description">{acquisition.projectDescription || '—'}</p>
                        </div>
                        <div className="detail-row">
                            <div className="detail-field">
                                <span className="detail-label">Klassifikation</span>
                                <span>{acquisition.classification || '—'}</span>
                            </div>
                            {acquisition.offerDate && (
                                <div className="detail-field">
                                    <span className="detail-label">Angebotsdatum</span>
                                    <span>{fmtDate(acquisition.offerDate)}</span>
                                </div>
                            )}
                        </div>
                        {acquisition.notes && (
                            <div className="detail-field">
                                <span className="detail-label">Notizen</span>
                                <p className="detail-description">{acquisition.notes}</p>
                            </div>
                        )}
                        {acquisition.lostReason && (
                            <div className="detail-field">
                                <span className="detail-label">Verlustgrund</span>
                                <p className="detail-description detail-description--lost">{acquisition.lostReason}</p>
                            </div>
                        )}
                    </section>

                    <section className="detail-section">
                        <h4>Angebotsdatei</h4>
                        {acquisition.offerFilePath ? (
                            <div className="offer-file-row">
                                <span className="offer-file-name">
                                    📎 {acquisition.offerFilePath.split('/').pop()}
                                </span>
                                <button className="button offer-file-btn" onClick={handleFileDownload}>
                                    Herunterladen
                                </button>
                                <button className="button-danger offer-file-btn" onClick={handleFileDelete}>
                                    Entfernen
                                </button>
                            </div>
                        ) : (
                            <div className="offer-file-upload">
                                <label className={`offer-file-label${uploading ? ' offer-file-uploading' : ''}`}>
                                    {uploading ? 'Wird hochgeladen…' : '+ Datei hochladen'}
                                    <input
                                        type="file"
                                        accept=".pdf,.doc,.docx,.xls,.xlsx,.zip"
                                        onChange={handleFileUpload}
                                        disabled={uploading}
                                        style={{ display: 'none' }}
                                    />
                                </label>
                                <span className="offer-file-hint">PDF, Word, Excel oder ZIP</span>
                            </div>
                        )}
                        {fileError && <p className="offer-file-error">{fileError}</p>}
                    </section>

                    <section className="detail-section">
                        <h4>Aktivitäten</h4>
                        <div className="activity-add">
                            <div className="activity-add-row">
                                <select
                                    value={activityType}
                                    onChange={(e) => setActivityType(e.target.value)}
                                    className="activity-type-select"
                                >
                                    <option value="call">📞 Anruf</option>
                                    <option value="email">✉️ E-Mail</option>
                                    <option value="meeting">🤝 Meeting</option>
                                    <option value="note">📝 Notiz</option>
                                </select>
                                <button
                                    className="button activity-submit"
                                    onClick={handleAddActivity}
                                    disabled={submitting || !activityContent.trim()}
                                >
                                    Hinzufügen
                                </button>
                            </div>
                            <textarea
                                className="activity-textarea"
                                placeholder="Was war das Ergebnis? Nächster Schritt?"
                                value={activityContent}
                                onChange={(e) => setActivityContent(e.target.value)}
                                rows={3}
                            />
                            <span className="activity-hint">
                                Anruf, E-Mail und Meeting zählen automatisch als Kontaktversuch.
                            </span>
                        </div>

                        <div className="activity-feed">
                            {activities.length === 0 && (
                                <p className="activity-empty">Noch keine Aktivitäten.</p>
                            )}
                            {activities.map((act) => (
                                <div key={act.id} className="activity-item">
                                    <div className="activity-icon">
                                        {ACTIVITY_ICONS[act.type] || '📌'}
                                    </div>
                                    <div className="activity-content">
                                        <div className="activity-meta">
                                            <span className="activity-user">{act.user_name || 'System'}</span>
                                            <span className="activity-time">
                                                {new Date(act.created_at).toLocaleString('de-DE', {
                                                    day: '2-digit', month: '2-digit', year: 'numeric',
                                                    hour: '2-digit', minute: '2-digit',
                                                })}
                                            </span>
                                        </div>
                                        <p className="activity-text">{act.content}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                </div>

                <div className="detail-footer">
                    <button className="button-warning" onClick={() => onEdit(acquisition)}>
                        Bearbeiten
                    </button>
                    <button className="button-danger" onClick={handleDelete}>
                        Löschen
                    </button>
                </div>
            </div>
        </div>
    );
}
